import React, { useEffect, useState, useCallback } from 'react';
import {
	getEmployeeNotes,
	createEmployeeNote,
	updateEmployeeNote,
	deleteEmployeeNote,
} from 'core/API/employeeNotes';
import { Box, Button, CircularProgress } from '@mui/material';
import { Save, Close, Edit, Delete } from '@mui/icons-material';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import dayjs from 'dayjs';
import { User, EmployeeNote } from 'types';

interface Props {
	user: User;
}

// Helper function to strip HTML tags from text
const stripHtmlTags = (html: string | undefined | null): string => {
	if (!html) return '';
	// Create a temporary div element to parse HTML
	const tmp = document.createElement('div');
	tmp.innerHTML = html;
	// Return text content without HTML tags
	return tmp.textContent || tmp.innerText || '';
};

const getIdFromAtId = (val: any) => {
	if (!val) return null;
	if (typeof val === 'number') return Number(val);
	if (val.id !== undefined && val.id !== null) return Number(val.id);
	if (typeof val === 'string' && val.includes('/')) {
		const parsed = parseInt(String(val).split('/').pop() || '0');
		return isNaN(parsed) ? null : parsed;
	}
	if (val['@id']) {
		const parsed = parseInt(String(val['@id']).split('/').pop() || '0');
		return isNaN(parsed) ? null : parsed;
	}
	return null;
};

const EmployeeNoteEdit: React.FC<Props> = ({ user }) => {
	const [notesLoading, setNotesLoading] = useState(false);
	const [notesError, setNotesError] = useState<string | null>(null);
	const [employeeNotes, setEmployeeNotes] = useState<EmployeeNote[]>([]);

	const [newNoteContent, setNewNoteContent] = useState('');
	const [creatingNewNote, setCreatingNewNote] = useState(false);

	const [editingNoteId, setEditingNoteId] = useState<number | null>(null);
	const [editContent, setEditContent] = useState('');
	const [savingNoteId, setSavingNoteId] = useState<number | null>(null);

	const fetchNotes = useCallback(async () => {
		setNotesLoading(true);
		setNotesError(null);
		try {
			const workplaceId =
				Number(user?.hierarchy?.id) || Number(localStorage.getItem('workplace-id')) || 1;
			const res = await getEmployeeNotes({ workplaceId });
			const list = Array.isArray(res?.member) ? res.member : res?.items || [];
			// Due to API mixup: the `author` field actually contains the user the note is assigned to.
			// Conversely `assignToUser` often contains the real creator. So we filter notes where
			// the stored author (assigned-to) matches the user being viewed.
			const isAssignedToUser = (note: any) => {
				const assignedField = note.author || note.author_user || note.assigned_to || null;
				const id = getIdFromAtId(assignedField);
				if (id !== null) return id === Number(user?.id);
				return false;
			};

			const filtered = (list as any[]).filter(isAssignedToUser);
			setEmployeeNotes(filtered as any[]);
		} catch (err: any) {
			setNotesError(err?.message || 'Failed to fetch employee notes');
		} finally {
			setNotesLoading(false);
		}
	}, [user]);

	useEffect(() => {
		fetchNotes();
	}, [fetchNotes]);

	const handleStartEdit = (note: EmployeeNote) => {
		const content = note.note || note.note_text || '';
		const id = getIdFromAtId(note?.['@id'] ? note['@id'] : note.id) || note.id;
		setEditingNoteId(id);
		// Strip HTML tags when loading content for editing
		setEditContent(stripHtmlTags(content));
	};

	const handleCancelEdit = () => {
		setEditingNoteId(null);
		setEditContent('');
	};

	const handleSaveEdit = async (note: EmployeeNote) => {
		const id = getIdFromAtId(note?.['@id'] ? note['@id'] : note.id) || note.id;
		setSavingNoteId(id);
		try {
			const workplaceId =
				Number(user?.hierarchy?.id) || Number(localStorage.getItem('workplace-id')) || 1;
			const assignedToId = getIdFromAtId(note?.author);
			await updateEmployeeNote(id, {
				note: editContent,
				workplaceId,
				authorId: Number(assignedToId), // the user the note is assigned to (API expects this in author)
				date: new Date().toISOString(),
			});
			setEditingNoteId(null);
			setEditContent('');
			await fetchNotes();
		} catch (err: any) {
			setNotesError(err?.message || 'Failed to update note');
		} finally {
			setSavingNoteId(null);
		}
	};

	const handleDeleteNote = async (note: any) => {
		const id = getIdFromAtId(note?.['@id'] ? note['@id'] : note.id) || note.id;
		if (!confirm('Are you sure you want to delete this note?')) return;
		try {
			await deleteEmployeeNote(id);
			await fetchNotes();
		} catch (err: any) {
			setNotesError(err?.message || 'Failed to delete note');
		}
	};

	const handleCreateNote = async () => {
		if (!newNoteContent || newNoteContent.trim() === '')
			return setNotesError('Note content is required');
		setCreatingNewNote(true);
		try {
			const workplaceId =
				Number(user?.hierarchy?.id) || Number(localStorage.getItem('workplace-id')) || 1;
			// When creating a note we want it to appear under the user being viewed (assigned to them),
			// while the actual creator is the logged-in user. Due to API mixup: set `author` to the viewed
			// user's id (assigned-to) and set `assignToUser` to the creator (auth.user.id).
			await createEmployeeNote({
				workplaceId,
				authorId: Number(user?.id), // assigned-to user per backend mixup
				note: newNoteContent,
				date: new Date().toISOString(),
			});
			setNewNoteContent('');
			await fetchNotes();
		} catch (err: any) {
			setNotesError(err?.message || 'Failed to create note');
		} finally {
			setCreatingNewNote(false);
		}
	};

	return (
		<div>
			{notesLoading && <div><CircularProgress size={20} color="inherit" /></div>}
			{notesError && <div style={{ color: 'red' }}>{notesError}</div>}
			{!notesLoading && !notesError && (
				<>
					{(!employeeNotes || employeeNotes.length === 0) && (
						<div>No employee notes assigned to this user.</div>
					)}

					<Box sx={{ mb: 2, p: 1, border: '1px dashed #ddd', borderRadius: 1 }}>
						<Typography variant="subtitle2">Add Note</Typography>
						<textarea
							style={{ width: '100%', minHeight: 80, padding: 8, marginTop: 8 }}
							value={newNoteContent}
							onChange={(e) => setNewNoteContent(e.target.value)}
						/>
						<Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
							<Button
								variant="contained"
								size="small"
								onClick={handleCreateNote}
								disabled={creatingNewNote}
								startIcon={<Save />}
								sx={{ backgroundColor: '#446388', '&:hover': { backgroundColor: '#354b6e' }, '&:disabled': { backgroundColor: '#cccccc', color: '#999999' } }}
							>
								Save
							</Button>
							<Button
								variant="outlined"
								size="small"
								onClick={() => setNewNoteContent('')}
								disabled={creatingNewNote}
								startIcon={<Close />}
								sx={{ borderColor: '#446388', color: '#446388', '&:hover': { borderColor: '#354b6e', backgroundColor: 'rgba(68, 99, 136, 0.04)' }, '&:disabled': { borderColor: '#cccccc', color: '#cccccc' } }}
							>
								Clear
							</Button>
						</Box>
					</Box>

					{employeeNotes && employeeNotes.length > 0 && (
						<div>
							{employeeNotes.map((note: EmployeeNote) => {
								const id = getIdFromAtId(note?.['@id'] ? note['@id'] : note.id) || note.id;
								const content = note.note || note.note_text || '';
								const date =
									note.date || note.date_of_the_note || note.createdAt || note.created_at || '';
								// Display author name from the field that actually contains the creator (assignToUser)
								const authorObj = note.assignToUser || null;
								const author = authorObj
									? `${authorObj.firstName || ''} ${authorObj.lastName || ''}`.trim()
									: '';

								return (
									<Box key={id} sx={{ mb: 2, p: 1, border: '1px solid #eee', borderRadius: 1 }}>
										<div
											style={{
												display: 'flex',
												justifyContent: 'space-between',
												alignItems: 'center',
												marginBottom: 6,
											}}
										>
											<div style={{ fontSize: 14 }}>
												<strong>{author || 'Unknown'}</strong> —{' '}
												{date ? dayjs(date).format('MM/DD/YYYY') : ''}
											</div>
											<div>
												{editingNoteId === id ? (
													<>
														<IconButton
															size="small"
															color="success"
															onClick={() => handleSaveEdit(note)}
															disabled={savingNoteId === id}
															aria-label="save-note"
														>
															<Save fontSize="small" />
														</IconButton>
														<IconButton
															size="small"
															color="default"
															onClick={handleCancelEdit}
															aria-label="cancel-edit"
														>
															<Close fontSize="small" />
														</IconButton>
													</>
												) : (
													<>
														<IconButton
															size="small"
															onClick={() => handleStartEdit(note)}
															aria-label="edit-note"
															sx={{ color: '#446388', '&:hover': { color: '#354b6e', backgroundColor: 'rgba(68, 99, 136, 0.04)' } }}
														>
															<Edit fontSize="small" />
														</IconButton>
														<IconButton
															size="small"
															color="error"
															onClick={() => handleDeleteNote(note)}
															aria-label="delete-note"
														>
															<Delete fontSize="small" />
														</IconButton>
													</>
												)}
											</div>
										</div>
										{editingNoteId === id ? (
											<textarea
												style={{ width: '100%', minHeight: 120, padding: 8 }}
												value={editContent}
												onChange={(e) => setEditContent(e.target.value)}
											/>
										) : (
											<div style={{ whiteSpace: 'pre-wrap' }}>{stripHtmlTags(content)}</div>
										)}
									</Box>
								);
							})}
						</div>
					)}
				</>
			)}
		</div>
	);
};

export default EmployeeNoteEdit;
